import { useState, useEffect, useRef } from "react";

import { Link, useNavigate, useLocation } from "react-router-dom";

import { useAuthStore } from "@/stores/auth-store";

import { useIsMobile } from "@/hooks/useIsMobile";

import {
  selectCurrencySymbol,
  useStoreSelectionStore,
} from "@/stores/store-selection-store";

import { useShallow } from "zustand/react/shallow";

import { BasketIcon } from "@/components/icons/SvgIcons";

import { useCartData } from "@/hooks/useCartData";

import MobileMenu from "./MobileMenu";

import HamburgerIcon from "../ui/HamburgerIcon";

import SecondaryNavigation from "./SecondaryNavigation";

import { DominosLogoBlue, DominosLogoWhite } from "../icons/SvgIcons";

import { useNavigation } from "@/contexts/NavigationContext";
import { testAttrs } from "@/utils/testAttrs";

interface HeaderProps {
  categories?: { id: number; name: string }[];

  activeCategory?: number;

  onCategoryChange?: (index: number) => void;
}

export default function Header({
  categories = [],

  activeCategory = 0,

  onCategoryChange,
}: HeaderProps) {
  const location = useLocation();

  const navigate = useNavigate();

  const isMobile = useIsMobile();

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const user = useAuthStore((s) => s.user);

  const logout = useAuthStore((s) => s.logout);
  const currencySymbol = useStoreSelectionStore(selectCurrencySymbol);

  const { cartCount, formattedTotal } = useCartData();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleHamburgerClick = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  useNavigation();

  const [isScrolled, setIsScrolled] = useState(false);
  const isScrolledRef = useRef(false);
  const headerRef = useRef<HTMLElement>(null);

  const BLACK_HEADER_HEIGHT = isMobile ? 0 : 118;

  const { storeId, service, postcode } = useStoreSelectionStore(
    useShallow((s) => ({
      storeId: s.storeId || "",

      service: s.service || "delivery",

      postcode: s.postcode || "",
    })),
  );

  const orderType = service.toLowerCase();

  const isAuthPage =
    location.pathname === "/login" || location.pathname === "/register";

  const isHomePage = location.pathname === "/";

  const isCheckoutPage =
    location.pathname.startsWith("/checkout") ||
    location.pathname.startsWith("/payment");

  const shouldShowStoreNav = !isAuthPage && !isHomePage && storeId;

  const isLegalPage = [
    "/terms-of-use",
    "/terms-and-conditions-of-sale",
    "/privacy-policy",
    "/copyright-and-legal",
    "/boring-legal-stuff",
    "/jointcontrollerstatement",
    "/contact-us",
    "/contact-form",
  ].includes(location.pathname);

  const isTextualPage = [
    "/terms-of-use",
    "/terms-and-conditions-of-sale",
    "/privacy-policy",
    "/copyright-and-legal",
    "/boring-legal-stuff",
  ].includes(location.pathname);

  const showHamburgerAndBasket = !isAuthPage && !isHomePage && (storeId || !isLegalPage);

  // Active tab for the mobile unified-row nav (scrolled state)
  const getActiveTab = (): "menu" | "deals" | null => {
    const pathname = location.pathname;
    if (
      pathname === "/menu" ||
      pathname.startsWith("/pizza-details") ||
      pathname.startsWith("/chicken-dip-details")
    )
      return "menu";
    if (pathname === "/deals" || pathname.startsWith("/deal-detail"))
      return "deals";
    return null;
  };
  const activeTab = getActiveTab();

  useEffect(() => {
    const handleScroll = () => {
      if (mobileMenuOpen) return;
      const currentScrollY = window.scrollY;

      // Update top via DOM directly to avoid a React re-render on every scroll pixel.
      if (headerRef.current && !isHomePage && !isTextualPage) {
        const top = Math.max(-BLACK_HEADER_HEIGHT, -currentScrollY);
        headerRef.current.style.top = `${top}px`;
      }

      // The SecondaryNavigation row collapses (max-h 80px → 0) when isScrolled
      // flips true. That removes up to 80px of document height, which the browser
      // compensates for by reducing scrollY. If the lower un-scroll threshold sat
      // below that collapse height, the clamp could drag scrollY back under it →
      // isScrolled flips false → height returns → scrollY jumps up → flips true:
      // an infinite up/down flicker. So the un-scroll (lower) boundary MUST stay
      // above the collapse height. We use a wide hysteresis band whose lower edge
      // (downThreshold) is comfortably above the 80px collapse.
      const mobileUpThreshold = window.innerWidth >= 768 ? 72 : 40;
      const upThreshold = isMobile ? mobileUpThreshold : BLACK_HEADER_HEIGHT;
      // Once scrolled, only un-scroll when we are clearly back near the very top —
      // below any height the collapse could reclaim — so the collapse can never
      // push us across this line.
      const downThreshold = isMobile ? 8 : Math.max(0, BLACK_HEADER_HEIGHT - 16);
      const COLLAPSE_HEIGHT = isMobile ? (window.innerWidth >= 768 ? 80 : 48) : 0;
      // Engage scrolled state only after passing the row height + the trigger
      // point, guaranteeing the post-collapse scrollY still sits above downThreshold.
      const engageAt = upThreshold + COLLAPSE_HEIGHT + 16;
      const nowScrolled = isScrolledRef.current
        ? currentScrollY > downThreshold
        : currentScrollY > engageAt;
      if (nowScrolled !== isScrolledRef.current) {
        isScrolledRef.current = nowScrolled;
        setIsScrolled(nowScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [BLACK_HEADER_HEIGHT, isMobile, mobileMenuOpen, isHomePage, isTextualPage]);

  const handleLogout = () => {
    logout();

    navigate("/login", { replace: true });

    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  // Shared mobile basket JSX — used in both the unified row (store pages) and
  // the collapsing section (non-store pages).
  const mobileBasket = storeId && isCheckoutPage ? (
    <div className="flex flex-col md:flex-row items-center justify-center gap-0 md:gap-2 h-full w-[66.74px] md:w-auto md:px-4 opacity-50 cursor-not-allowed shrink-0 relative before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-[24px] before:w-[1px] before:bg-white before:border-l before:border-[#ccc] md:before:content-none">
      <div className="relative">
        <BasketIcon width={20} height={20} className="text-dominos-green shrink-0" />
        {cartCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-dominos-red-bright text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
            {cartCount}
          </span>
        )}
      </div>
      <span className="text-dominos-green text-[10px] md:text-[14px] dominos-secondary leading-tight">
        {currencySymbol}{formattedTotal}
      </span>
    </div>
  ) : (
    <Link
      to={`/basket?postcode=${postcode}&storeId=${encodeURIComponent(storeId)}&orderType=${orderType}`}
      className="flex flex-col md:flex-row items-center justify-center gap-0 md:gap-2 h-full w-[66.74px] md:w-auto md:px-4 hover:bg-gray-100 shrink-0 relative before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-[24px] before:w-[1px] before:bg-white before:border-l before:border-[#ccc] md:before:content-none"
      aria-label="View basket"
      {...testAttrs({ "data-testid": "basket-link" })}
    >
      <div className="relative">
        <BasketIcon width={20} height={20} className="text-dominos-green shrink-0" />
        {cartCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-dominos-red-bright text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
            {cartCount}
          </span>
        )}
      </div>
      <span className="text-dominos-green text-[10px] md:text-[14px] dominos-secondary leading-tight">
        {currencySymbol}{formattedTotal}
      </span>
    </Link>
  );

  return (
    <>
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        isScrolled={isScrolled}
      />

      <header
        ref={headerRef}
        className={`${isTextualPage ? "relative" : "sticky top-0"} left-0 z-50 w-full ${isMobile ? "bg-white pt-[4px]" : "bg-[#212121]"}`}
      >
        {isMobile && !!shouldShowStoreNav && !isAuthPage && (
          <div className="relative flex items-center w-full h-[40px] md:h-[72px] bg-white">

            {/* LEFT section — auto-width on mobile, 100px on tablet.
                Separator lives on this wrapper at its right edge on tablet,
                so both sides are always equidistant from their viewport edges. */}
            <div className="flex items-center h-full relative md:w-[104px] md:after:content-[''] md:after:absolute md:after:right-0 md:after:top-1/2 md:after:-translate-y-1/2 md:after:h-[24px] md:after:w-[1px] md:after:bg-white md:after:border-l md:after:border-[#ccc]">
              {showHamburgerAndBasket ? (
                <HamburgerIcon
                  isOpen={mobileMenuOpen}
                  onClick={handleHamburgerClick}
                  className="!w-auto !pl-4 !pr-4 md:!pl-[40px] md:!pr-[40px] md:after:content-none"
                />
              ) : (
                <div className="w-10" />
              )}
            </div>

            {/* Spacer — fills the center between the two fixed-width side columns */}
            <div className="flex-1" />

            {/* Logo — absolutely centred across the FULL row width on both
                mobile and tablet so it sits at true 50% regardless of
                how wide the hamburger or basket columns are. */}
            <div
              className={`absolute inset-y-0 left-0 right-0 flex items-center justify-center pointer-events-none transition-opacity duration-300 ${
                isScrolled ? "opacity-0" : "opacity-100"
              }`}
            >
              <Link
                to={storeId ? `/menu?postcode=${postcode}&storeId=${encodeURIComponent(storeId)}&orderType=${orderType}` : "/"}
                className="pointer-events-auto"
                {...testAttrs({ "data-testid": "header-logo" })}
              >
                <DominosLogoBlue className="w-[118px] h-[26px] md:w-[194px] md:h-[42px]" />
              </Link>
            </div>

            {/* MENU/DEALS — absolute across the FULL row width, fades in when scrolled */}
            <nav
              className={`absolute inset-y-0 left-0 right-0 flex items-center justify-center pointer-events-none transition-opacity duration-300 ${
                isScrolled ? "opacity-100" : "opacity-0"
              }`}
            >
              <Link
                to={`/menu?postcode=${postcode}&storeId=${encodeURIComponent(storeId)}&orderType=${orderType}`}
                {...testAttrs({ "data-testid": "nav-tab", "data-tab-name": "menu" })}
                className={`pointer-events-auto px-4 md:px-[36px] h-[40px] md:h-[72px] pt-[8px] pb-[8px] nav-font text-[14px] md:text-[36px] uppercase whitespace-nowrap transition-colors relative border-b-2 ${
                  activeTab === "menu"
                    ? "text-dominos-red-bright border-dominos-blue"
                    : "text-dominos-gray border-transparent"
                }`}
              >
                Menu
                {activeTab === "menu" && (
                  <div
                    className="absolute z-50 left-1/2 -translate-x-1/2 bottom-0 w-0 h-0 pointer-events-none"
                    style={{
                      borderLeft: "8px solid transparent",
                      borderRight: "8px solid transparent",
                      borderBottom: "8px solid hsl(var(--dominos-blue))",
                    }}
                  />
                )}
              </Link>
              <Link
                to={`/deals?postcode=${postcode}&storeId=${encodeURIComponent(storeId)}&orderType=${orderType}`}
                {...testAttrs({ "data-testid": "nav-tab", "data-tab-name": "deals" })}
                className={`pointer-events-auto px-4 md:px-[36px] h-[40px] md:h-[72px] pt-[8px] pb-[8px] nav-font text-[14px] md:text-[36px] uppercase whitespace-nowrap transition-colors relative border-b-2 ${
                  activeTab === "deals"
                    ? "text-dominos-red-bright border-dominos-blue"
                    : "text-dominos-gray border-transparent"
                }`}
              >
                Deals
                {activeTab === "deals" && (
                  <div
                    className="absolute z-50 left-1/2 -translate-x-1/2 bottom-0 w-0 h-0 pointer-events-none"
                    style={{
                      borderLeft: "8px solid transparent",
                      borderRight: "8px solid transparent",
                      borderBottom: "8px solid hsl(var(--dominos-blue))",
                    }}
                  />
                )}
              </Link>
            </nav>

            {/* RIGHT section — auto-width on mobile, 100px on tablet.
                Separator lives on this wrapper at its left edge on tablet. */}
            <div className="flex items-center justify-end h-full relative md:w-[104px] md:before:content-[''] md:before:absolute md:before:left-0 md:before:top-1/2 md:before:-translate-y-1/2 md:before:h-[24px] md:before:w-[1px] md:before:bg-white md:before:border-l md:before:border-[#ccc]">
              {showHamburgerAndBasket && storeId ? mobileBasket : <div className="w-10" />}
            </div>
          </div>
        )}

        {/*
         * MOBILE — NON-STORE PAGES
         * Original collapsing logo section (homepage, legal, auth pages).
         */}
        {isMobile && !shouldShowStoreNav && (
          <div
            className={`overflow-hidden flex items-center justify-center transition-all duration-300 ${
              isScrolled ? "max-h-0 py-0" : "max-h-20 py-2 md:py-4"
            }`}
          >
            {!isAuthPage && (
              <div className="flex items-center justify-between px-4 py-0 w-full h-[40px] md:h-[72px]">
                {showHamburgerAndBasket ? (
                  <HamburgerIcon
                    isOpen={mobileMenuOpen}
                    onClick={handleHamburgerClick}
                  />
                ) : (
                  <div className="w-6" />
                )}

                <Link
                  to={storeId ? `/menu?postcode=${postcode}&storeId=${encodeURIComponent(storeId)}&orderType=${orderType}` : "/"}
                  className="flex items-center justify-center"
                  {...testAttrs({ "data-testid": "header-logo" })}
                >
                  <DominosLogoBlue className="w-[118px] h-[26px] md:w-[194px] md:h-[42px] ml-[16px] md:ml-0 mt-[-4px] md:mt-0 desktop:w-[216px] desktop:h-12" />
                </Link>

                {showHamburgerAndBasket ? mobileBasket : <div className="w-6" />}
              </div>
            )}
          </div>
        )}

        {/* DESKTOP */}
        {!isMobile && (
          <div>
            <div className="page-container h-full py-6">
              <div className="grid-12 h-full items-center relative">
                {!isAuthPage && (
                  <>
                    <div className="col-start-6 col-span-2 col-end-8 flex items-center justify-center">
                      <Link
                        to={storeId ? `/menu?postcode=${postcode}&storeId=${encodeURIComponent(storeId)}&orderType=${orderType}` : "/"}
                        className="inline-flex items-center justify-center -translate-y-1.5"
                        {...testAttrs({ "data-testid": "header-logo" })}
                      >
                        <DominosLogoWhite
                          height={54}
                          className="cursor-pointer"
                        />
                      </Link>
                    </div>

                    <nav className="col-start-9 col-span-4 self-end justify-self-end text-right py-4">
                      <div className="flex items-center justify-end text-white text-[16px] font-normal mb-8 leading-[20.8px] uppercase space-x-2">
                        {!isAuthenticated && (
                          <>
                            <Link to="/register" className="hover:underline">
                              REGISTER
                            </Link>

                            <span>|</span>

                            <Link to="/login" className="hover:underline">
                              LOG IN
                            </Link>
                          </>
                        )}

                        {isAuthenticated && (
                          <>
                            <span>{user?.name || "User"}</span>

                            <span>|</span>

                            <button
                              type="button"
                              onClick={() => void handleLogout()}
                              className="hover:underline"
                            >
                              LOG OUT
                            </button>
                          </>
                        )}
                      </div>
                    </nav>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {shouldShowStoreNav && (
          <div className="sticky z-40 bg-white">
            <SecondaryNavigation isScrolled={isScrolled} showShadow={isTextualPage} />
          </div>
        )}

        {shouldShowStoreNav && categories.length > 0 && !mobileMenuOpen && (
          <div className="bg-dominos-light-gray overflow-y-hidden scrollbar-hide">
            <div className="page-container">
              <div className="flex items-center gap-6 lg:gap-10 px-3 flex-nowrap w-fit mx-auto">
                {categories.map((cat, idx) => (
                  <button
                    key={cat.id}
                    {...testAttrs({ "data-testid": "category-tab", "data-category-name": cat.name, "data-category-idx": idx })}
                    className={`relative nav-font py-2 md:px-8 px-4 md:text-[24px] text-[14px] uppercase cursor-pointer flex flex-col items-center transition-colors duration-150 whitespace-nowrap ${
                      idx === activeCategory
                        ? "text-dominos-red-bright border-b-2 border-dominos-blue"
                        : "text-dominos-blue hover:text-dominos-red-bright"
                    }`}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      onCategoryChange?.(idx);
                    }}
                  >
                    {cat.name}

                    {idx === activeCategory && (
                      <div className="absolute z-0 -bottom-2.5 right-0 left-0 mx-auto transform w-3 h-3 bg-dominos-blue rotate-45 pointer-events-none"></div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
